import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Trash2, Pause, Play, Plus, X, Unlock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", is_paid: false });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
            setUsers(u.data);
            setStats(s.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const update = async (user_id, patch) => {
        try {
            await api.patch(`/admin/users/${user_id}`, patch);
            toast.success("Updated");
            refresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (user_id, email) => {
        if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${user_id}`);
            toast.success("User deleted");
            refresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const unlock = async (user_id, email) => {
        try {
            const { data } = await api.post(`/admin/users/${user_id}/unlock`);
            toast.success(data.cleared ? `Unlocked ${email}` : `${email} wasn't locked`);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const createUser = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/users", form);
            toast.success("User created");
            setCreateOpen(false);
            setForm({ name: "", email: "", password: "", role: "user", is_paid: false });
            refresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <Helmet><title>Admin — OpenCSV</title></Helmet>
            <section className="mx-auto max-w-7xl px-4 md:px-8 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ admin</div>
                        <h1 className="font-display font-black tracking-tighter text-4xl">Operations</h1>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Link to="/admin/leads" className="border border-ink/20 px-3 py-2.5 text-sm ui-heading" data-testid="admin-leads-link">Leads queue</Link>
                        <Link to="/admin/questions" className="border border-ink/20 px-3 py-2.5 text-sm ui-heading" data-testid="admin-questions-link">Q&amp;A pages</Link>
                        <Link to="/admin/inbox" className="border border-ink/20 px-3 py-2.5 text-sm ui-heading" data-testid="admin-inbox-link">Inbox</Link>
                        <button onClick={() => setCreateOpen(true)} className="btn-brutal bg-ink text-white px-4 py-2.5 ui-heading text-sm font-semibold inline-flex items-center gap-2" data-testid="admin-new-user-button">
                            <Plus size={14} /> New user
                        </button>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-ink/10 border border-ink/10 mb-8" data-testid="admin-stats">
                        {[
                            ["Total users", stats.total_users],
                            ["Paid", stats.paid_users],
                            ["Suspended", stats.suspended_users],
                            ["Files saved", stats.total_files],
                            ["Revenue", `$${stats.total_revenue_usd}`],
                            ["Inbox unread", stats.inbox_unread ?? 0],
                        ].map(([label, value]) => (
                            <div key={label} className="bg-white p-5">
                                <div className="ui-heading text-[11px] uppercase tracking-widest text-ink-muted">{label}</div>
                                <div className="mt-1 font-display font-black text-3xl tracking-tighter">{value}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="border border-black/10 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-paper border-b border-black/10">
                            <tr className="text-left">
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Role</th>
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 ui-heading text-xs uppercase tracking-wider">Provider</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-users-table">
                            {loading ? (
                                <tr><td colSpan={7} className="p-6 text-center text-ink-muted">Loading…</td></tr>
                            ) : users.map((u) => (
                                <tr key={u.user_id} className="border-b border-black/5" data-testid={`admin-row-${u.email}`}>
                                    <td className="px-4 py-3">{u.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                    <td className="px-4 py-3 capitalize">{u.role}</td>
                                    <td className="px-4 py-3">{u.is_paid ? "Lifetime" : "Free"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 text-[11px] ui-heading ${u.status === "suspended" ? "bg-signal-red/10 text-signal-red" : "bg-signal-green/10 text-signal-green"}`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 capitalize text-ink-muted">{u.auth_provider}</td>
                                    <td className="px-4 py-3 flex items-center gap-1 justify-end">
                                        <button title={u.is_paid ? "Revoke paid" : "Grant lifetime"} onClick={() => update(u.user_id, { is_paid: !u.is_paid })} className="px-2 py-1 border border-ink/15 ui-heading text-xs hover:border-cobalt" data-testid={`toggle-paid-${u.email}`}>
                                            {u.is_paid ? "Revoke" : "Grant $1"}
                                        </button>
                                        <button title={u.status === "suspended" ? "Unsuspend" : "Suspend"} onClick={() => update(u.user_id, { status: u.status === "suspended" ? "active" : "suspended" })} className="px-2 py-1 border border-ink/15 hover:border-cobalt" data-testid={`toggle-status-${u.email}`}>
                                            {u.status === "suspended" ? <Play size={12} /> : <Pause size={12} />}
                                        </button>
                                        <button title="Clear login lockout" onClick={() => unlock(u.user_id, u.email)} className="px-2 py-1 border border-cobalt/40 text-cobalt hover:bg-cobalt/5" data-testid={`unlock-user-${u.email}`}>
                                            <Unlock size={12} />
                                        </button>
                                        <button title="Delete" onClick={() => remove(u.user_id, u.email)} className="px-2 py-1 border border-signal-red/40 text-signal-red" data-testid={`delete-user-${u.email}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {createOpen && (
                <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="create-user-modal">
                    <div className="bg-white border border-ink w-full max-w-md p-6 shadow-brutal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display font-black text-2xl">New user</h3>
                            <button onClick={() => setCreateOpen(false)}><X size={16} /></button>
                        </div>
                        <form onSubmit={createUser} className="space-y-3">
                            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-ink/20 px-3 py-2 focus-cobalt" data-testid="new-user-name" />
                            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-ink/20 px-3 py-2 focus-cobalt" data-testid="new-user-email" />
                            <input required minLength={6} type="password" placeholder="Password (6+ chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-ink/20 px-3 py-2 focus-cobalt" data-testid="new-user-password" />
                            <div className="flex gap-3 items-center">
                                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-ink/20 px-3 py-2" data-testid="new-user-role">
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                                <label className="ui-heading text-sm flex items-center gap-2">
                                    <input type="checkbox" checked={form.is_paid} onChange={(e) => setForm({ ...form, is_paid: e.target.checked })} data-testid="new-user-paid" />
                                    Grant lifetime
                                </label>
                            </div>
                            <button type="submit" className="btn-brutal w-full bg-cobalt text-white px-4 py-2.5 ui-heading text-sm font-semibold border border-cobalt" data-testid="create-user-submit">
                                Create
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
